import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import type { User as SupabaseUser, Session } from '@supabase/supabase-js';

export type AppRole = 'admin' | 'monteur' | 'teamleiter' | 'office';

interface AuthUser {
  id: string;
  email: string;
  name: string;
  roles: AppRole[];
  module_performance_visible: boolean;
  module_fahrzeuglager_visible: boolean;
}

interface AuthContextType {
  user: AuthUser | null;
  session: Session | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<{ error?: string }>;
  signUp: (email: string, password: string, name: string) => Promise<{ error?: string }>;
  signInWithGoogle: () => Promise<void>;
  resetPassword: (email: string) => Promise<{ error?: string }>;
  signOut: () => Promise<void>;
  hasRole: (role: AppRole) => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

async function fetchUserProfile(userId: string): Promise<AuthUser | null> {
  try {
    const [profileRes, rolesRes] = await Promise.all([
      supabase.from('profiles').select('name, module_performance_visible, module_fahrzeuglager_visible').eq('user_id', userId).maybeSingle(),
      supabase.from('user_roles').select('role').eq('user_id', userId),
    ]);


    const { data: userData } = await supabase.auth.getUser();

    return {
      id: userId,
      email: userData.user?.email || '',
      name: profileRes.data?.name || '',
      roles: (rolesRes.data || []).map(r => r.role as AppRole),
      module_performance_visible: profileRes.data?.module_performance_visible ?? true,
      module_fahrzeuglager_visible: profileRes.data?.module_fahrzeuglager_visible ?? true,
    };
  } catch (err) {
    console.error('[Auth] fetchUserProfile error:', err);
    return null;
  }
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  // Set up auth listener — NO async work inside onAuthStateChange
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, newSession) => {
      setSession(newSession);
      setSession(newSession);
      if (!newSession) {
        setUser(null);
        setLoading(false);
      }
    });

    supabase.auth.getSession().then(({ data: { session: existingSession } }) => {
      setSession(existingSession);
      setSession(existingSession);
      if (!existingSession) {
        setLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  // Fetch profile whenever session changes — separate effect to avoid blocking auth
  useEffect(() => {
    if (!session?.user) return;
    
    fetchUserProfile(session.user.id).then(profile => {
      setUser(profile);
      setLoading(false);
    });
  }, [session?.user?.id]);

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) return { error: error.message };
    return {};
  };

  const signUp = async (email: string, password: string, name: string) => {
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: window.location.origin,
        data: { full_name: name },
      },
    });
    if (error) return { error: error.message };
    return {};
  };

  const signInWithGoogle = async () => {
    const { lovable } = await import('@/integrations/lovable');
    await lovable.auth.signInWithOAuth('google', {
      redirect_uri: window.location.origin,
    });
  };

  const resetPassword = async (email: string) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    if (error) return { error: error.message };
    return {};
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setSession(null);
  };

  const hasRole = (role: AppRole) => user?.roles.includes(role) ?? false;

  return (
    <AuthContext.Provider value={{ user, session, loading, signIn, signUp, signInWithGoogle, resetPassword, signOut, hasRole }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};
